// src/pages/Posts.jsx - Enhanced with pagination and grid view
import React, { useState , useEffect} from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { postsAPI } from '../services/api';
import { motion } from 'framer-motion';
import {
    CheckIcon,
    XMarkIcon,
    ArrowPathIcon,
    RocketLaunchIcon,
    TrashIcon,
    EyeIcon,
    FunnelIcon,
    MagnifyingGlassIcon,
    CalendarDaysIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    DocumentDuplicateIcon,
    Cog6ToothIcon,
    Squares2X2Icon,
    ListBulletIcon,
    ChevronLeftIcon,
    ChevronRightIcon
} from '@heroicons/react/24/outline';
import PostCard from '../components/dashboard/PostCard';
import toast from 'react-hot-toast';

function Posts() {
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [selectedPost, setSelectedPost] = useState(null);
    const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'
    const [currentPage, setCurrentPage] = useState(1);
    const [postsPerPage] = useState(12); // Fixed number per page

    // Full-screen image state
    const [fullScreenImage, setFullScreenImage] = useState(null);

    // Editing states for the expanded post
    const [isEditingCaption, setIsEditingCaption] = useState(false);
    const [editedCaption, setEditedCaption] = useState('');
    const [isEditingHashtags, setIsEditingHashtags] = useState(false);
    const [editedHashtags, setEditedHashtags] = useState('');
    const [isEditingPrompt, setIsEditingPrompt] = useState(false);
    const [editedPrompt, setEditedPrompt] = useState('');

    // Platform editing state
    const [showPlatformSelector, setShowPlatformSelector] = useState(false);
    const [selectedPlatforms, setSelectedPlatforms] = useState([]);

    const queryClient = useQueryClient();

    const { data: posts, isLoading, error } = useQuery('posts', postsAPI.list, {
        refetchInterval: 30000, // Refresh every 30 seconds for status updates
    });

    const { data: availablePlatforms } = useQuery(
        'availablePlatforms',
        postsAPI.getAvailablePlatforms,
        { staleTime: 5 * 60 * 1000 }
    );

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, statusFilter]);

    // Existing mutations
    const approveMutation = useMutation(
        ({ id, variant }) => postsAPI.approve(id, variant),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('posts');
                toast.success('Post approved!');
            },
            onError: (error) => {
                toast.error(error.response?.data?.message || 'Failed to approve post');
            },
        }
    );

    const publishMutation = useMutation(postsAPI.publish, {
        onSuccess: () => {
            queryClient.invalidateQueries('posts');
            toast.success('Publishing started!');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to publish post');
        },
    });

    const deleteMutation = useMutation(postsAPI.delete, {
        onSuccess: () => {
            queryClient.invalidateQueries('posts');
            toast.success('Post deleted');
            setSelectedPost(null);
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to delete post');
        },
    });





    const retryMutation = useMutation(postsAPI.retry, {
        onSuccess: () => {
            queryClient.invalidateQueries('posts');
            toast.success('Post retry started!');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to retry post');
        },
    });

    // New mutations for editing features
    const updateCaptionMutation = useMutation(
        ({ id, caption }) => postsAPI.updateCaption(id, caption),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('posts');
                toast.success('Caption updated!');
                setIsEditingCaption(false);
            },
            onError: (error) => {
                toast.error(error.response?.data?.message || 'Failed to update caption');
            },
        }
    );

    const updateHashtagsMutation = useMutation(
        ({ id, hashtags }) => postsAPI.updateHashtags(id, hashtags),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('posts');
                toast.success('Hashtags updated!');
                setIsEditingHashtags(false);
            },
            onError: (error) => {
                toast.error(error.response?.data?.message || 'Failed to update hashtags');
            },
        }
    );

    const updatePromptAndRegenerateMutation = useMutation(
        ({ id, prompt }) => postsAPI.updatePromptAndRegenerate(id, prompt),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('posts');
                toast.success('Image regeneration started with new prompt!');
                setIsEditingPrompt(false);
            },
            onError: (error) => {
                toast.error(error.response?.data?.message || 'Failed to regenerate images');
            },
        }
    );

    const updatePlatformsMutation = useMutation(
        ({ id, platforms }) => postsAPI.updatePlatforms(id, platforms),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('posts');
                toast.success('Platforms updated!');
                setShowPlatformSelector(false);
            },
            onError: (error) => {
                toast.error(error.response?.data?.message || 'Failed to update platforms');
            },
        }
    );

    const cloneMutation = useMutation(
        (id) => postsAPI.clone(id),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('posts');
                toast.success('Post cloned successfully!');
            },
            onError: (error) => {
                toast.error(error.response?.data?.message || 'Failed to clone post');
            },
        }
    );

    // Full-screen image handlers
    const handleImageClick = (imageUrl, variant, postTitle) => {
        setFullScreenImage({ url: imageUrl, variant, title: postTitle });
    };

    const closeFullScreen = () => {
        setFullScreenImage(null);
    };

    // Caption editing handlers
    const handleEditCaption = (post) => {
        setEditedCaption(post.caption || '');
        setIsEditingCaption(true);
    };

    const handleCancelEditCaption = (post) => {
        setEditedCaption(post.caption || '');
        setIsEditingCaption(false);
    };

    const handleSaveCaption = (post) => {
        if (editedCaption.trim() === '') {
            toast.error('Caption cannot be empty');
            return;
        }

        updateCaptionMutation.mutate({
            id: post._id,
            caption: editedCaption.trim()
        });
    };

    // Hashtag editing handlers
    const handleEditHashtags = (post) => {
        setEditedHashtags((post.hashtags || []).join(' '));
        setIsEditingHashtags(true);
    };

    const handleCancelEditHashtags = (post) => {
        setEditedHashtags((post.hashtags || []).join(' '));
        setIsEditingHashtags(false);
    };

    const handleSaveHashtags = (post) => {
        const hashtagArray = editedHashtags
            .split(/[\s,]+/)
            .map(tag => tag.trim())
            .filter(tag => tag.length > 0)
            .map(tag => tag.startsWith('#') ? tag : `#${tag}`)
            .filter(tag => tag.length > 1);

        updateHashtagsMutation.mutate({
            id: post._id,
            hashtags: hashtagArray
        });
    };

    // Prompt editing handlers
    const handleEditPrompt = (post) => {
        setEditedPrompt(post.imagePrompt || '');
        setIsEditingPrompt(true);
    };

    const handleCancelEditPrompt = (post) => {
        setEditedPrompt(post.imagePrompt || '');
        setIsEditingPrompt(false);
    };

    const handleSavePrompt = (post) => {
        if (editedPrompt.trim() === '') {
            toast.error('Image prompt cannot be empty');
            return;
        }

        if (window.confirm('This will regenerate both images with the new prompt. Continue?')) {
            updatePromptAndRegenerateMutation.mutate({
                id: post._id,
                prompt: editedPrompt.trim()
            });
        }
    };

    // Platform editing handlers
    const handleOpenPlatformSelector = (post) => {
        setSelectedPlatforms([...post.platforms]);
        setShowPlatformSelector(true);
    };

    const handleClosePlatformSelector = (post) => {
        setSelectedPlatforms([...post.platforms]);
        setShowPlatformSelector(false);
    };

    const handlePlatformToggle = (platformId) => {
        setSelectedPlatforms(prev => {
            if (prev.includes(platformId)) {
                return prev.filter(p => p !== platformId);
            } else {
                return [...prev, platformId];
            }
        });
    };

    const handleSavePlatforms = (post) => {
        if (selectedPlatforms.length === 0) {
            toast.error('Please select at least one platform');
            return;
        }

        updatePlatformsMutation.mutate({
            id: post._id,
            platforms: selectedPlatforms
        });
    };

    // Basic handlers
    const handleApprove = (post, variant) => {
        approveMutation.mutate({ id: post._id, variant });
    };

    const handlePublish = (post) => {
        if (window.confirm('Publish this post immediately?')) {
            publishMutation.mutate(post._id);
        }
    };

    const handleDelete = (post) => {
        if (window.confirm('Are you sure you want to delete this post?')) {
            deleteMutation.mutate(post._id);
        }
    };

    const handleRetry = (post) => {
        retryMutation.mutate(post._id);
    };

    const handleClone = (post) => {
        if (window.confirm('Clone this post?')) {
            cloneMutation.mutate(post._id);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center py-12">
                <div className="text-red-600 mb-4">Failed to load posts</div>
                <button
                    onClick={() => queryClient.invalidateQueries('posts')}
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                >
                    Reset
                </button>
            </div>
        );
    }

    const postsData = posts?.data?.data || posts?.data || [];
    const postsArray = Array.isArray(postsData) ? postsData : [];
    const platforms = availablePlatforms?.data?.data || [];

    // Filter posts based on search and status
    const filteredPosts = postsArray.filter(post => {
        const matchesSearch = !searchTerm ||
            post.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            post.caption?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = statusFilter === 'all' || post.status === statusFilter;

        return matchesSearch && matchesStatus;
    });

    // Pagination calculations
    const totalPosts = filteredPosts.length;
    const totalPages = Math.ceil(totalPosts / postsPerPage);
    const startIndex = (currentPage - 1) * postsPerPage;
    const endIndex = startIndex + postsPerPage;
    const currentPosts = filteredPosts.slice(startIndex, endIndex);

    const getStatusBadge = (status) => {
        const badges = {
            queued: 'bg-yellow-100 text-yellow-800',
            approved: 'bg-green-100 text-green-800',
            posting: 'bg-blue-100 text-blue-800',
            posted: 'bg-purple-100 text-purple-800',
            failed: 'bg-red-100 text-red-800',
        };
        return badges[status] || 'bg-gray-100 text-gray-800';
    };

    const getDisplayImageUrls = (post) => {
        return {
            sora: post.imageMetadata?.sora?.publicUrl || post.imageUrl_sora,
            whisk: post.imageMetadata?.whisk?.publicUrl || post.imageUrl_whisk
        };
    };

    const statusCounts = postsArray.reduce((acc, post) => {
        acc[post.status] = (acc[post.status] || 0) + 1;
        return acc;
    }, {});

    // Pagination component
    const Pagination = () => {
        const getPageNumbers = () => {
            const pages = [];
            const showPages = 5; // Number of page buttons to show
            let startPage = Math.max(1, currentPage - Math.floor(showPages / 2));
            let endPage = Math.min(totalPages, startPage + showPages - 1);

            // Adjust start page if we're near the end
            if (endPage - startPage + 1 < showPages) {
                startPage = Math.max(1, endPage - showPages + 1);
            }

            for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
            }
            return pages;
        };

        if (totalPages <= 1) return null;

        return (
            <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
                <div className="flex flex-1 justify-between sm:hidden">
                    <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Previous
                    </button>
                    <button
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Next
                    </button>
                </div>
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                    <div>
                        <p className="text-sm text-gray-700">
                            Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                            <span className="font-medium">{Math.min(endIndex, totalPosts)}</span> of{' '}
                            <span className="font-medium">{totalPosts}</span> results
                        </p>
                    </div>
                    <div>
                        <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                            <button
                                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                disabled={currentPage === 1}
                                className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <span className="sr-only">Previous</span>
                                <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                            </button>
                            {getPageNumbers().map((page) => (
                                <button
                                    key={page}
                                    onClick={() => setCurrentPage(page)}
                                    className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                                        currentPage === page
                                            ? 'z-10 bg-blue-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                                            : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                                    }`}
                                >
                                    {page}
                                </button>
                            ))}
                            <button
                                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                disabled={currentPage === totalPages}
                                className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <span className="sr-only">Next</span>
                                <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                            </button>
                        </nav>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-gray-900">Posts Management</h1>
                <p className="mt-2 text-sm text-gray-700">
                    View, manage, and publish your social media posts
                </p>
            </div>

            {/* Stats Bar */}
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-6">
                {[
                    { label: 'Total', count: postsArray.length, color: 'bg-gray-100 text-gray-800' },
                    { label: 'Queued', count: statusCounts.queued || 0, color: 'bg-yellow-100 text-yellow-800' },
                    { label: 'Approved', count: statusCounts.approved || 0, color: 'bg-green-100 text-green-800' },
                    { label: 'Posting', count: statusCounts.posting || 0, color: 'bg-blue-100 text-blue-800' },
                    { label: 'Posted', count: statusCounts.posted || 0, color: 'bg-purple-100 text-purple-800' },
                    { label: 'Failed', count: statusCounts.failed || 0, color: 'bg-red-100 text-red-800' }
                ].map((stat) => (
                    <div key={stat.label} className="bg-white overflow-hidden shadow rounded-lg">
                        <div className="p-5">
                            <div className="flex items-center">
                                <div className="flex-1">
                                    <div className="text-sm font-medium text-gray-500">{stat.label}</div>
                                    <div className="mt-1 text-3xl font-semibold text-gray-900">{stat.count}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Filters and View Toggle */}
            <div className="bg-white shadow rounded-lg p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    {/* Search */}
                    <div className="relative flex-1 max-w-md">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search posts..."
                            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                    </div>

                    <div className="flex items-center space-x-4">
                        {/* Status Filter */}
                        <div className="flex items-center space-x-2">
                            <FunnelIcon className="h-5 w-5 text-gray-400" />
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="block pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                            >
                                <option value="all">All Status</option>
                                <option value="queued">Queued</option>
                                <option value="approved">Approved</option>
                                <option value="posting">Posting</option>
                                <option value="posted">Posted</option>
                                <option value="failed">Failed</option>
                            </select>
                        </div>

                        {/* View Toggle */}
                        <div className="flex items-center space-x-1 bg-gray-100 p-1 rounded-md">
                            <button
                                onClick={() => setViewMode('list')}
                                className={`p-2 rounded ${
                                    viewMode === 'list'
                                        ? 'bg-white text-gray-900 shadow-sm'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                                title="List view"
                            >
                                <ListBulletIcon className="h-4 w-4" />
                            </button>
                            <button
                                onClick={() => setViewMode('grid')}
                                className={`p-2 rounded ${
                                    viewMode === 'grid'
                                        ? 'bg-white text-gray-900 shadow-sm'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                                title="Grid view"
                            >
                                <Squares2X2Icon className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Results summary */}
                <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
                    <div>
                        Showing {currentPosts.length} of {filteredPosts.length} posts
                        {filteredPosts.length !== postsArray.length && ` (filtered from ${postsArray.length})`}
                    </div>
                    <div>
                        Page {currentPage} of {totalPages}
                    </div>
                </div>
            </div>

            {/* Posts Display */}
            {filteredPosts.length === 0 ? (
                <div className="text-center py-12 bg-white shadow rounded-lg">
                    <h3 className="mt-2 text-sm font-medium text-gray-900">
                        {searchTerm || statusFilter !== 'all' ? 'No posts match your filters' : 'No posts yet'}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                        {searchTerm || statusFilter !== 'all'
                            ? 'Try adjusting your search or filter criteria'
                            : 'Generate your first social media post to get started'
                        }
                    </p>
                </div>
            ) : viewMode === 'grid' ? (
                // Grid View (like dashboard)
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {currentPosts.map((post) => (
                        <PostCard key={post._id} post={post} />
                    ))}
                </div>
            ) : (
                // List View (existing detailed view)
                <div className="bg-white shadow rounded-lg overflow-hidden">
                    <div className="divide-y divide-gray-200">
                        {currentPosts.map((post) => {
                            const displayUrls = getDisplayImageUrls(post);
                            const isExpanded = selectedPost === post._id;
                            const canEditContent = ['queued', 'approved'].includes(post.status);
                            const canEditPrompt = ['queued', 'approved', 'failed'].includes(post.status);

                            return (
                                <motion.div
                                    key={post._id}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="p-6 hover:bg-gray-50 transition-colors"
                                >
                                    {/* Post Header */}
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-3 mb-2">
                                                <h3 className="text-lg font-medium text-gray-900">
                                                    {post.title || 'Untitled Post'}
                                                </h3>
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(post.status)}`}>
                                                    {post.status}
                                                </span>
                                            </div>

                                            <div className="text-sm text-gray-600 mb-3 line-clamp-2">
                                                {post.caption}
                                            </div>

                                            {/* Hashtags */}
                                            {post.hashtags && post.hashtags.length > 0 && (
                                                <div className="flex flex-wrap gap-1 mb-3">
                                                    {post.hashtags.map((tag, index) => (
                                                        <span
                                                            key={index}
                                                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                                        >
                                                            {tag}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Platforms */}
                                            <div className="flex items-center space-x-4 mb-3">
                                                <div className="text-sm text-gray-500">
                                                    <span className="font-medium">Platforms:</span>
                                                    <div className="flex items-center space-x-2 mt-1">
                                                        {post.platforms && post.platforms.length > 0 ? (
                                                            post.platforms.map((platformId) => {
                                                                const platform = platforms.find(p => p.id === platformId);
                                                                return (
                                                                    <span
                                                                        key={platformId}
                                                                        className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700"
                                                                    >
                                                                        <span className="mr-1">{platform?.icon || '🌐'}</span>
                                                                        {platform?.name || platformId}
                                                                    </span>
                                                                );
                                                            })
                                                        ) : (
                                                            <span className="text-gray-400 text-xs">No platforms selected</span>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="text-sm text-gray-500">
                                                    <CalendarDaysIcon className="h-4 w-4 inline mr-1" />
                                                    {new Date(post.createdAt).toLocaleDateString()}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Actions */}
                                        <div className="flex items-center space-x-2 ml-4">
                                            <button
                                                onClick={() => setSelectedPost(isExpanded ? null : post._id)}
                                                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                            >
                                                <EyeIcon className="h-4 w-4 mr-1" />
                                                {isExpanded ? 'Hide' : 'View'}
                                            </button>

                                            {post.status === 'approved' && (
                                                <button
                                                    onClick={() => handlePublish(post)}
                                                    disabled={publishMutation.isLoading}
                                                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                                                >
                                                    <RocketLaunchIcon className="h-4 w-4 mr-1" />
                                                    Publish
                                                </button>
                                            )}

                                            {(post.status === 'queued' || post.status === 'failed') && (
                                                <button
                                                    onClick={() => handleRetry(post)}
                                                    disabled={retryMutation.isLoading}
                                                    className={`inline-flex items-center px-3 py-2 border text-sm leading-4 font-medium rounded-md disabled:opacity-50 ${
                                                        post.status === 'failed'
                                                            ? 'border-red-300 text-red-700 bg-red-50 hover:bg-red-100'
                                                            : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                                                    }`}
                                                >
                                                    <ArrowPathIcon className="h-4 w-4 mr-1" />
                                                    Reset
                                                </button>
                                            )}

                                            <button
                                                onClick={() => handleClone(post)}
                                                disabled={cloneMutation.isLoading}
                                                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                                            >
                                                <DocumentDuplicateIcon className="h-4 w-4 mr-1" />
                                                {cloneMutation.isLoading ? 'Cloning...' : 'Clone'}
                                            </button>

                                            <button
                                                onClick={() => handleDelete(post)}
                                                disabled={deleteMutation.isLoading}
                                                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
                                            >
                                                <TrashIcon className="h-4 w-4 mr-1" />
                                                Delete
                                            </button>
                                        </div>
                                    </div>

                                    {/* Expanded Content */}
                                    {isExpanded && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            exit={{ opacity: 0, height: 0 }}
                                            className="mt-6 pt-6 border-t border-gray-200"
                                        >
                                            {/* Caption Editing */}
                                            {isEditingCaption ? (
                                                <div className="mb-4">
                                                    <textarea
                                                        value={editedCaption}
                                                        onChange={(e) => setEditedCaption(e.target.value)}
                                                        className="w-full p-3 border border-gray-300 rounded-md text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                        rows={4}
                                                        placeholder="Enter post caption..."
                                                        maxLength={2000}
                                                    />
                                                    <div className="flex items-center justify-between mt-2">
                                                        <div className="text-xs text-gray-500">
                                                            {editedCaption.length}/2000 characters
                                                        </div>
                                                        <div className="flex space-x-2">
                                                            <button
                                                                onClick={() => handleCancelEditCaption(post)}
                                                                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                                                            >
                                                                Cancel
                                                            </button>
                                                            <button
                                                                onClick={() => handleSaveCaption(post)}
                                                                disabled={updateCaptionMutation.isLoading || editedCaption.trim() === ''}
                                                                className="inline-flex items-center px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50"
                                                            >
                                                                {updateCaptionMutation.isLoading ? (
                                                                    <>
                                                                        <ArrowPathIcon className="h-3 w-3 mr-1 animate-spin" />
                                                                        Saving...
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <CheckIcon className="h-3 w-3 mr-1" />
                                                                        Save
                                                                    </>
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="mb-4">
                                                    <h4 className="text-sm font-medium text-gray-900 mb-2">Caption:</h4>
                                                    <div className="bg-gray-50 rounded-lg p-3">
                                                        <div className="text-sm text-gray-700 whitespace-pre-wrap">{post.caption}</div>
                                                    </div>
                                                    {canEditContent && (
                                                        <button
                                                            onClick={() => handleEditCaption(post)}
                                                            className="mt-2 text-xs text-blue-600 hover:text-blue-700 hover:underline"
                                                        >
                                                            Edit caption
                                                        </button>
                                                    )}
                                                </div>
                                            )}

                                            {/* Hashtag Editing */}
                                            {isEditingHashtags ? (
                                                <div className="mb-4">
                                                    <textarea
                                                        value={editedHashtags}
                                                        onChange={(e) => setEditedHashtags(e.target.value)}
                                                        className="w-full p-3 border border-gray-300 rounded-md text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                        rows={2}
                                                        placeholder="Enter hashtags separated by spaces (e.g. #marketing #socialmedia #content)"
                                                        maxLength={500}
                                                    />
                                                    <div className="flex items-center justify-between mt-2">
                                                        <div className="text-xs text-gray-500">
                                                            {editedHashtags.length}/500 characters • Separate hashtags with spaces
                                                        </div>
                                                        <div className="flex space-x-2">
                                                            <button
                                                                onClick={() => handleCancelEditHashtags(post)}
                                                                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                                                            >
                                                                Cancel
                                                            </button>
                                                            <button
                                                                onClick={() => handleSaveHashtags(post)}
                                                                disabled={updateHashtagsMutation.isLoading}
                                                                className="inline-flex items-center px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50"
                                                            >
                                                                {updateHashtagsMutation.isLoading ? (
                                                                    <>
                                                                        <ArrowPathIcon className="h-3 w-3 mr-1 animate-spin" />
                                                                        Saving...
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <CheckIcon className="h-3 w-3 mr-1" />
                                                                        Save
                                                                    </>
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="mb-4">
                                                    <h4 className="text-sm font-medium text-gray-900 mb-2">Hashtags:</h4>
                                                    <div className="bg-gray-50 rounded-lg p-3">
                                                        {post.hashtags && post.hashtags.length > 0 ? (
                                                            <div className="flex flex-wrap gap-1">
                                                                {post.hashtags.map((tag, index) => (
                                                                    <span
                                                                        key={index}
                                                                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                                                    >
                                                                        {tag}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <div className="text-xs text-gray-400">No hashtags</div>
                                                        )}
                                                    </div>
                                                    {canEditContent && (
                                                        <button
                                                            onClick={() => handleEditHashtags(post)}
                                                            className="mt-2 text-xs text-blue-600 hover:text-blue-700 hover:underline"
                                                        >
                                                            {post.hashtags && post.hashtags.length > 0 ? 'Edit hashtags' : 'Add hashtags'}
                                                        </button>
                                                    )}
                                                </div>
                                            )}



                                            {/* Images */}
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                                {updatePromptAndRegenerateMutation.isLoading ? (
                                                    // Loading state during regeneration
                                                    <>
                                                        <div className="relative bg-gray-100 rounded-lg h-48 flex items-center justify-center">
                                                            <div className="text-center">
                                                                <ArrowPathIcon className="h-8 w-8 text-gray-400 animate-spin mx-auto mb-2" />
                                                                <div className="text-sm text-gray-500">Generating Sora image...</div>
                                                            </div>
                                                        </div>
                                                        <div className="relative bg-gray-100 rounded-lg h-48 flex items-center justify-center">
                                                            <div className="text-center">
                                                                <ArrowPathIcon className="h-8 w-8 text-gray-400 animate-spin mx-auto mb-2" />
                                                                <div className="text-sm text-gray-500">Generating Whisk image...</div>
                                                            </div>
                                                        </div>
                                                    </>
                                                ) : (
                                                    // Normal image display
                                                    <>
                                                        {displayUrls.sora && (
                                                            <div className={`relative ${post.status === 'approved' && post.imageUrl_final === post.imageUrl_sora ? 'ring-2 ring-green-500' : ''}`}>
                                                                <img
                                                                    src={displayUrls.sora}
                                                                    alt="OpenAI generated"
                                                                    className="w-full h-full object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                                                                    onClick={() => handleImageClick(displayUrls.sora, 'OpenAI', post.title)}
                                                                    onError={(e) => {
                                                                        console.error('Failed to load OpenAI image:', displayUrls.sora);
                                                                        e.target.style.display = 'none';
                                                                    }}
                                                                />
                                                                <div className="absolute top-2 left-2">
                                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-white bg-opacity-90 text-gray-900">
                                                                        OpenAI {post.status === 'approved' && post.imageUrl_final === post.imageUrl_sora && '✅'}
                                                                    </span>
                                                                </div>
                                                                {post.status === 'queued' && (
                                                                    <div className="absolute bottom-2 left-2 right-2">
                                                                        <button
                                                                            onClick={() => handleApprove(post, 'sora')}
                                                                            disabled={approveMutation.isLoading}
                                                                            className="w-full bg-green-600 text-white px-3 py-1 rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                                                                        >
                                                                            ✅ Approve
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}

                                                        {displayUrls.whisk && (
                                                            <div className={`relative ${post.status === 'approved' && post.imageUrl_final === post.imageUrl_whisk ? 'ring-2 ring-green-500' : ''}`}>
                                                                <img
                                                                    src={displayUrls.whisk}
                                                                    alt="Google Gemini generated"
                                                                    className="w-full h-full object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                                                                    onClick={() => handleImageClick(displayUrls.whisk, 'Google Gemini', post.title)}
                                                                    onError={(e) => {
                                                                        console.error('Failed to load Google Gemini image:', displayUrls.whisk);
                                                                        e.target.style.display = 'none';
                                                                    }}
                                                                />
                                                                <div className="absolute top-2 left-2">
                                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-white bg-opacity-90 text-gray-900">
                                                                        Google Gemini {post.status === 'approved' && post.imageUrl_final === post.imageUrl_whisk && '✅'}
                                                                    </span>
                                                                </div>
                                                                {post.status === 'queued' && (
                                                                    <div className="absolute bottom-2 left-2 right-2">
                                                                        <button
                                                                            onClick={() => handleApprove(post, 'whisk')}
                                                                            disabled={approveMutation.isLoading}
                                                                            className="w-full bg-green-600 text-white px-3 py-1 rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                                                                        >
                                                                            ✅ Approve
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>

                                            {/* Image Prompt Editing */}
                                            {isEditingPrompt && (
                                                <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                                                    <h4 className="text-sm font-medium text-amber-800 mb-2">Edit Image Prompt</h4>
                                                    <textarea
                                                        value={editedPrompt}
                                                        onChange={(e) => setEditedPrompt(e.target.value)}
                                                        className="w-full p-3 border border-amber-300 rounded-md text-sm resize-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                                        rows={3}
                                                        placeholder="Describe the image you want to generate..."
                                                        maxLength={1000}
                                                    />
                                                    <div className="flex items-center justify-between mt-2">
                                                        <div className="text-xs text-amber-600">
                                                            {editedPrompt.length}/1000 characters • This will regenerate both OpenAI and Google Gemini images
                                                        </div>
                                                        <div className="flex space-x-2">
                                                            <button
                                                                onClick={() => handleCancelEditPrompt(post)}
                                                                className="px-3 py-1 text-sm text-amber-700 hover:text-amber-800"
                                                            >
                                                                Cancel
                                                            </button>
                                                            <button
                                                                onClick={() => handleSavePrompt(post)}
                                                                disabled={updatePromptAndRegenerateMutation.isLoading || editedPrompt.trim() === ''}
                                                                className="inline-flex items-center px-3 py-1 bg-amber-600 text-white text-sm rounded-md hover:bg-amber-700 disabled:opacity-50"
                                                            >
                                                                {updatePromptAndRegenerateMutation.isLoading ? (
                                                                    <>
                                                                        <ArrowPathIcon className="h-3 w-3 mr-1 animate-spin" />
                                                                        Regenerating...
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <RocketLaunchIcon className="h-3 w-3 mr-1" />
                                                                        Regenerate Images
                                                                    </>
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Edit Prompt Button */}
                                            {!isEditingPrompt && canEditPrompt && (
                                                <div className="mb-6 flex justify-center">
                                                    <button
                                                        onClick={() => handleEditPrompt(post)}
                                                        className="inline-flex items-center px-4 py-2 border border-amber-300 text-sm font-medium rounded-md text-amber-700 bg-amber-50 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition-colors"
                                                    >
                                                        <Cog6ToothIcon className="h-4 w-4 mr-2" />
                                                        Edit Image Prompt & Regenerate
                                                    </button>
                                                </div>
                                            )}

                                            {/* Platform Selector */}
                                            {showPlatformSelector && canEditContent && (
                                                <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                                                    <div className="flex items-center justify-between mb-3">
                                                        <h4 className="text-sm font-medium text-gray-900">Select Platforms</h4>
                                                        <button
                                                            onClick={() => handleClosePlatformSelector(post)}
                                                            className="text-gray-400 hover:text-gray-600"
                                                        >
                                                            <XMarkIcon className="h-5 w-5" />
                                                        </button>
                                                    </div>

                                                    <div className="grid grid-cols-2 gap-3 mb-4">
                                                        {platforms.map((platform) => (
                                                            <label
                                                                key={platform.id}
                                                                className={`flex items-center p-3 rounded-md border cursor-pointer transition-colors ${
                                                                    selectedPlatforms.includes(platform.id)
                                                                        ? 'border-blue-300 bg-blue-50'
                                                                        : 'border-gray-200 hover:border-gray-300'
                                                                }`}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedPlatforms.includes(platform.id)}
                                                                    onChange={() => handlePlatformToggle(platform.id)}
                                                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                                />
                                                                <span className="ml-3 text-lg">{platform.icon}</span>
                                                                <span className="ml-2 text-sm font-medium text-gray-900">
                                                                    {platform.name}
                                                                </span>
                                                            </label>
                                                        ))}
                                                    </div>

                                                    <div className="flex items-center justify-between">
                                                        <div className="text-xs text-gray-500">
                                                            {selectedPlatforms.length} platform{selectedPlatforms.length !== 1 ? 's' : ''} selected
                                                        </div>
                                                        <div className="flex space-x-2">
                                                            <button
                                                                onClick={() => handleClosePlatformSelector(post)}
                                                                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                                                            >
                                                                Cancel
                                                            </button>
                                                            <button
                                                                onClick={() => handleSavePlatforms(post)}
                                                                disabled={updatePlatformsMutation.isLoading || selectedPlatforms.length === 0}
                                                                className="inline-flex items-center px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50"
                                                            >
                                                                {updatePlatformsMutation.isLoading ? (
                                                                    <>
                                                                        <ArrowPathIcon className="h-3 w-3 mr-1 animate-spin" />
                                                                        Saving...
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <CheckIcon className="h-3 w-3 mr-1" />
                                                                        Save
                                                                    </>
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Platform info with edit button */}
                                            <div className="mb-6">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center space-x-4">
                                                        <div className="text-sm text-gray-500">
                                                            <span className="font-medium">Platforms:</span>
                                                            <div className="flex items-center space-x-2 mt-1">
                                                                {post.platforms && post.platforms.length > 0 ? (
                                                                    post.platforms.map((platformId) => {
                                                                        const platform = platforms.find(p => p.id === platformId);
                                                                        return (
                                                                            <span
                                                                                key={platformId}
                                                                                className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700"
                                                                            >
                                                                                <span className="mr-1">{platform?.icon || '🌐'}</span>
                                                                                {platform?.name || platformId}
                                                                            </span>
                                                                        );
                                                                    })
                                                                ) : (
                                                                    <span className="text-gray-400 text-xs">No platforms selected</span>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {canEditContent && !showPlatformSelector && (
                                                            <button
                                                                onClick={() => handleOpenPlatformSelector(post)}
                                                                className="inline-flex items-center px-2 py-1 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
                                                                title="Edit platforms"
                                                            >
                                                                <Cog6ToothIcon className="h-3 w-3 mr-1" />
                                                                Edit
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Publishing Results */}
                                            {post.status === 'posted' && post.publishingAttempts && post.publishingAttempts.length > 0 && (
                                                <div className="mb-4">
                                                    <h4 className="text-sm font-medium text-gray-900 mb-3">Publishing Results:</h4>
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                        {post.publishingAttempts.map((attempt, index) => {
                                                            // Special handling for LinkedIn with multiple accounts
                                                            if (attempt.platform === 'linkedin' && attempt.linkedinDetails) {
                                                                return attempt.linkedinDetails.map((detail, detailIndex) => (
                                                                    <div
                                                                        key={`${index}-${detailIndex}`}
                                                                        className={`flex items-center justify-between px-3 py-2 rounded text-sm ${
                                                                            detail.success
                                                                                ? 'bg-green-50 text-green-700'
                                                                                : 'bg-red-50 text-red-700'
                                                                        }`}
                                                                    >
                                                                        <div className="flex items-center">
                                                                            {detail.success ? (
                                                                                <CheckCircleIcon className="h-4 w-4 mr-2" />
                                                                            ) : (
                                                                                <ExclamationTriangleIcon className="h-4 w-4 mr-2" />
                                                                            )}
                                                                            <span className="font-medium">
                                                                                LinkedIn ({detail.accountType}):
                                                                            </span>
                                                                            <span className="ml-1">
                                                                                {detail.success ? 'Published' : 'Failed'}
                                                                            </span>
                                                                        </div>
                                                                        {detail.success && detail.postUrl && (
                                                                            <a
                                                                                href={detail.postUrl}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="ml-2 text-blue-600 hover:text-blue-800 underline text-xs"
                                                                                onClick={(e) => e.stopPropagation()}
                                                                            >
                                                                                View Post →
                                                                            </a>
                                                                        )}
                                                                    </div>
                                                                ));
                                                            }

                                                            // Regular handling for other platforms
                                                            return (
                                                                <div
                                                                    key={index}
                                                                    className={`flex items-center justify-between px-3 py-2 rounded text-sm ${
                                                                        attempt.success
                                                                            ? 'bg-green-50 text-green-700'
                                                                            : 'bg-red-50 text-red-700'
                                                                    }`}
                                                                >
                                                                    <div className="flex items-center">
                                                                        {attempt.success ? (
                                                                            <CheckCircleIcon className="h-4 w-4 mr-2" />
                                                                        ) : (
                                                                            <ExclamationTriangleIcon className="h-4 w-4 mr-2" />
                                                                        )}
                                                                        <span className="capitalize font-medium">{attempt.platform}:</span>
                                                                        <span className="ml-1">
                                                                            {attempt.success ? 'Published' : 'Failed'}
                                                                        </span>
                                                                        {!attempt.success && attempt.error && (
                                                                            <span className="ml-2 truncate text-xs" title={attempt.error}>
                                                                                - {attempt.error.substring(0, 30)}...
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    {attempt.success && (attempt.postUrl || attempt.tweetUrl) && (
                                                                        <a
                                                                            href={attempt.postUrl || attempt.tweetUrl}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="ml-2 text-blue-600 hover:text-blue-800 underline text-xs"
                                                                            onClick={(e) => e.stopPropagation()}
                                                                        >
                                                                            View Post →
                                                                        </a>
                                                                    )}
                                                                </div>
                                                            );
                                                        }).flat()}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Metadata */}
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-gray-500">
                                                <div>
                                                    <span className="font-medium">Created:</span> {new Date(post.createdAt).toLocaleString()}
                                                </div>
                                                <div>
                                                    <span className="font-medium">Updated:</span> {new Date(post.updatedAt).toLocaleString()}
                                                </div>
                                                {post.publishedAt && (
                                                    <div>
                                                        <span className="font-medium">Published:</span> {new Date(post.publishedAt).toLocaleString()}
                                                    </div>
                                                )}
                                                {post.sourceUrl && (
                                                    <div>
                                                        <span className="font-medium">Source:</span>
                                                        <a href={post.sourceUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 ml-1">
                                                            View Source
                                                        </a>
                                                    </div>
                                                )}
                                                {post.imagePrompt && (
                                                    <div className="col-span-2">
                                                        <span className="font-medium">Image Prompt:</span>
                                                        <div className="mt-1 text-xs text-gray-600 bg-gray-50 p-2 rounded">
                                                            {post.imagePrompt}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </motion.div>
                                    )}
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Pagination */}
            <Pagination />

            {/* Full Screen Image Modal */}
            {fullScreenImage && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
                    onClick={closeFullScreen}
                >
                    <div className="relative max-w-full max-h-full">
                        <img
                            src={fullScreenImage.url}
                            alt={`${fullScreenImage.variant} generated - Full screen`}
                            className="max-w-full max-h-full object-contain"
                            onClick={(e) => e.stopPropagation()}
                        />
                        <button
                            onClick={closeFullScreen}
                            className="absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70 transition-colors"
                        >
                            <XMarkIcon className="h-6 w-6" />
                        </button>
                        <div className="absolute bottom-4 left-4 text-white bg-black bg-opacity-50 px-3 py-1 rounded-full">
                            {fullScreenImage.variant} variant - {fullScreenImage.title}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Posts;